<template>
  <div class="flex h-full flex-col">
    <!-- Header/Filter if needed -->
    <div class="flex justify-between gap-4">
      <!-- Add search/filter here if needed -->
      <SmInput
        v-model="search"
        outline
        design="form"
        size="medium"
        :label="i18n.t('browserExtension.searchDeploymentNameOrId')"
        class="deployments-search-input max-w-xs" />

      <SmButton
        class="ml-auto"
        :text="i18n.t('browserExtension.createDeployment')"
        @click="createDeployment" />
    </div>

    <div v-if="loadingData" class="mt-6 flex flex-col gap-2">
      <SmSkeleton v-for="i in 5" :key="i" height="40px" width="100%" />
    </div>

    <EmptyState
      v-else-if="!filteredDeployments.length"
      :icon="Server"
      :title="i18n.t('browserExtension.noDeployments')"
      :text="i18n.t('browserExtension.noDeploymentsDescription')"
      :create-label="i18n.t('browserExtension.createDeployment')"
      :show-docs-btn="false"
      @create="createDeployment" />

    <div v-else class="mt-6 min-h-0 flex-1 overflow-hidden">
      <SmTable
        :data="filteredDeployments"
        :columns="columns"
        class="h-full"
        :loading="loadingData"
        key-field="_id"
        @row-click="handleRowClick">
        <template #name="{ row }">
          <div class="flex flex-col">
            <TextMultiline class="font-medium">{{ row.name }}</TextMultiline>
            <TextMultiline class="font-mono text-xs text-gray-500">
              {{ row._id }}
            </TextMultiline>
          </div>
        </template>
        <template #token="{ row }">
          <div class="group flex h-full w-full items-center gap-4" @click.stop>
            <p v-if="token[row._id]" class="flex items-center gap-4">
              <span>{{ formattedToken(row._id) }}</span>
              <el-tooltip
                :content="i18n.t('browserExtension.copyToken')"
                placement="top">
                <Copy
                  :size="16"
                  class="hover:text-blue-600 cursor-pointer text-contrast transition-colors"
                  @click="copyToken(row._id)" />
              </el-tooltip>
            </p>
            <SmButton
              v-else
              size="smaller"
              outline
              @click="requestToken(row._id)">
              <span>{{ i18n.t('browserExtension.requestToken') }}</span>
            </SmButton>
          </div>
        </template>
        <template #created="{ row }">
          <p>{{ displayDate(dayjs(row.created).toDate()) }}</p>
        </template>
        <template #actions="{ row }">
          <div @click.stop>
            <SmDropdown>
              <SmDropdownItem :icon="PencilLine" @click="editDeployment(row)">
                {{ i18n.t('edit') }}
              </SmDropdownItem>

              <SmDropdownItem :icon="Trash" @click="deleteDeployment(row)">
                {{ i18n.t('delete') }}
              </SmDropdownItem>

              <SmDropdownItem
                :icon="KeyRound"
                class="stroke-contrast"
                @click="copyToken(row._id)">
                {{ i18n.t('browserExtension.copyToken') }}
              </SmDropdownItem>
            </SmDropdown>
          </div>
        </template>
      </SmTable>
    </div>

    <DeploymentDetailDrawer
      v-model="drawerVisible"
      :deployment-id="selectedDeploymentId"
      :deployment-name="selectedDeploymentName" />

    <SmDialog
      v-model:visibility="editDialogVisible"
      :title="
        editMode
          ? i18n.t('browserExtension.editDeployment')
          : i18n.t('browserExtension.createDeployment')
      "
      size="medium">
      <el-form
        v-model="form.fields"
        :model="form.fields"
        label-width="150px"
        label-position="top"
        require-asterisk-position="right">
        <el-form-item
          :label="i18n.t('name')"
          prop="name"
          required
          :error="form.getErrorMessage('name')">
          <SmInput
            v-model="form.fields.name"
            size="large"
            design="form"
            outline />
        </el-form-item>

        <el-form-item
          :label="i18n.t('description')"
          prop="description"
          :error="form.getErrorMessage('description')">
          <SmInput
            v-model="form.fields.description"
            size="large"
            design="form"
            outline />
        </el-form-item>
      </el-form>

      <template #footer>
        <div class="flex items-center gap-2">
          <SmButton outline @click="editDialogVisible = false">
            {{ i18n.t('cancel') }}
          </SmButton>
          <SmButton @click="handleSave">
            {{ editMode ? i18n.t('save') : i18n.t('create') }}
          </SmButton>
        </div>
      </template>
    </SmDialog>

    <DeleteConfirmDialog
      v-model:visible="deleteConfirmDialogVisible"
      :title="i18n.t('browserExtension.deleteDeployment')"
      :sub-title="
        i18n.t('browserExtension.deleteDeploymentText', {
          name: deploymentToDelete?.name,
        })
      "
      @cancel="deleteConfirmDialogVisible = false"
      @delete="handleDelete" />
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch, onUnmounted } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { Server, Copy, PencilLine, Trash, KeyRound } from 'lucide-vue-next'
  import dayjs from 'dayjs'
  import {
    ApiError,
    BrowserDeployment,
    BrowserDeploymentTokenOut,
    BrowserExtensionManagementService,
  } from '@/client'
  import EmptyState from '@/components/shared/EmptyState.vue'
  import SmInput from '@/components/sm/SmInput.vue'
  import SmDialog from '@/components/sm/SmDialog.vue'
  import { isRequired } from '@/components/sm/SmInput/SmInputValidator'
  import useForm from '@/composables/useForm'
  import { sendToast } from '@/components/sm/SmNotification'
  import {
    useBrowserDeployments,
    createDeployment as createDeploymentInStore,
    updateDeployment as updateDeploymentInStore,
    deleteDeployment as deleteDeploymentInStore,
  } from '@/stores/browserextension/browserextension.store'
  import { displayDate } from '@/utils/formatter/date'
  import { capitalize } from '@/utils/formatter/formatter'
  import { Column } from '../../sm/SmTable.types'
  import DeploymentDetailDrawer from '../drawers/DeploymentDetailDrawer.vue'

  const i18n = useI18n()
  const loadingData = ref(true)
  const search = ref('')

  const { data: deployments, unsubscribe: unsubDeployments } =
    useBrowserDeployments()

  // Cleanup subscriptions on unmount to prevent memory leaks
  onUnmounted(() => {
    unsubDeployments()
  })

  watch(
    deployments,
    (newVal) => {
      if (newVal !== undefined) {
        loadingData.value = false
      }
    },
    { immediate: true }
  )

  const filteredDeployments = computed(() => {
    if (!deployments.value) return []
    if (!search.value) return deployments.value
    const s = search.value.toLowerCase()
    return deployments.value.filter(
      (d) => d.name.toLowerCase().includes(s) || d._id.toLowerCase().includes(s)
    )
  })

  // Drawer
  const drawerVisible = ref(false)
  const selectedDeploymentId = ref<string | null>(null)
  const selectedDeploymentName = ref<string | undefined>(undefined)

  // Deployment token
  const token = ref<Record<string, BrowserDeploymentTokenOut>>({})

  // Create/Edit dialog
  const editDialogVisible = ref(false)
  const editMode = ref(false)
  const deploymentToEdit = ref<BrowserDeployment | null>(null)

  // Delete dialog
  const deleteConfirmDialogVisible = ref(false)
  const deploymentToDelete = ref<BrowserDeployment | null>(null)

  const form = useForm(
    {
      name: '',
      description: '',
    },
    {
      onReset: function () {},
    },
    {
      name: [isRequired],
    },
    {}
  )

  function createDeployment() {
    deploymentToEdit.value = null
    editMode.value = false
    form.reset()
    form.clearError()
    editDialogVisible.value = true
  }

  function handleRowClick(index: number, row: BrowserDeployment) {
    selectedDeploymentId.value = row._id
    selectedDeploymentName.value = row.name
    drawerVisible.value = true
  }

  function editDeployment(deployment: BrowserDeployment) {
    deploymentToEdit.value = deployment
    editMode.value = true
    form.fields.name = deployment.name
    form.fields.description = deployment.description
    form.clearError()
    editDialogVisible.value = true
  }

  function deleteDeployment(deployment: BrowserDeployment) {
    deploymentToDelete.value = deployment
    deleteConfirmDialogVisible.value = true
  }

  function formattedToken(deploymentId: string) {
    if (token.value[deploymentId]) {
      const deploymentToken = token.value[deploymentId].deployment_token
      const last8 = deploymentToken.slice(-8)
      return 'XXXX XXXX ' + last8.slice(4)
    }
    return ''
  }

  async function requestToken(id: string) {
    try {
      const response =
        await BrowserExtensionManagementService.createBrowserDeploymentTokenApiV1BrowserDeploymentsBrowserDeploymentIdTokenPost(
          {
            browserDeploymentId: id,
          }
        )
      token.value[id] = response
    } catch (error) {
      const apiError = error as ApiError
      console.error(`Failed to load token for deployment ${id}:`, apiError)
      sendToast('', i18n.t('browserExtension.copyToClipboardFailed'), 'error')
    }
  }

  function copyToken(id: string) {
    if (token.value[id]?.deployment_token) {
      navigator.clipboard
        .writeText(token.value[id].deployment_token)
        .then(function () {
          sendToast(
            '',
            i18n.t('browserExtension.copyToClipboardSuccess'),
            'success'
          )
        })
        .catch(function (error) {
          console.error('Failed to copy token:', error)
          sendToast(
            '',
            i18n.t('browserExtension.copyToClipboardFailed'),
            'error'
          )
        })
      return
    }

    sendToast('', i18n.t('browserExtension.copyToClipboardFailed'), 'error')
  }

  async function handleSave() {
    const validation = form.validate()
    if (!validation) {
      sendToast('', i18n.t('general.form.fillInAllFields.message'), 'error')
      return
    }

    try {
      if (editMode.value && deploymentToEdit.value) {
        await updateDeploymentInStore(deploymentToEdit.value._id, form.fields)
        sendToast('', i18n.t('browserExtension.deploymentSaved'), 'success')
      } else {
        await createDeploymentInStore(form.fields)
        sendToast('', i18n.t('browserExtension.deploymentCreated'), 'success')
      }

      editDialogVisible.value = false
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      sendToast('', message, 'error')
    }
  }

  async function handleDelete() {
    if (!deploymentToDelete.value) {
      return
    }

    try {
      await deleteDeploymentInStore(deploymentToDelete.value._id)
      deleteConfirmDialogVisible.value = false
      sendToast('', i18n.t('browserExtension.deleteDeployment'), 'success')
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      sendToast('', message, 'error')
    }
  }

  watch(editDialogVisible, function (isVisible) {
    if (!isVisible) {
      form.reset()
      form.clearError()
      deploymentToEdit.value = null
      editMode.value = false
    }
  })

  const columns = computed<Column<BrowserDeployment>[]>(() => [
    {
      key: 'name',
      label: capitalize(i18n.t('name')),
      width: 2,
      sortable: true,
      loader: true,
    },
    {
      key: 'token',
      label: i18n.t('browserExtension.requestToken'),
      width: 1,
      sortable: false,
      loader: true,
    },
    {
      key: 'created',
      label: capitalize(i18n.t('browserExtension.created')),
      width: 1,
      sortable: true,
      loader: true,
    },
    {
      key: 'actions',
      label: '',
      width: '56px',
      sortable: false,
      loader: true,
    },
  ])
</script>

<style scoped lang="scss">
  .deployments-search-input :deep(.sm-input) {
    height: 1.5rem;
  }
</style>
